import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface AlertItem {
  orderId: string;
  restaurantId: string;
}

interface OrderAlertState {
  queue: AlertItem[];
  // Tracks every orderId that has ever been enqueued OR seen during a backfill
  // flush this session so that SSE reconnect replays never re-trigger the alert
  // dialog for the same order.
  shownIds: Set<string>;
  enqueue: (orderId: string, restaurantId: string) => void;
  // Mark an order as seen without enqueuing it (called for orders suppressed
  // during the initial backfill flush so they survive reconnects).
  markSeen: (orderId: string) => void;
  dequeue: () => void;
  dismiss: (orderId: string) => void;
}

export const useOrderAlertStore = create<OrderAlertState>()(
  persist(
    (set) => ({
      queue: [],
      shownIds: new Set<string>(),
      enqueue: (orderId, restaurantId) =>
        set((s) => {
          if (s.shownIds.has(orderId)) return s;
          return {
            queue: [...s.queue, { orderId, restaurantId }],
            shownIds: new Set([...s.shownIds, orderId]),
          };
        }),
      markSeen: (orderId) =>
        set((s) => {
          if (s.shownIds.has(orderId)) return s;
          return { shownIds: new Set([...s.shownIds, orderId]) };
        }),
      dequeue: () => set((s) => ({ queue: s.queue.slice(1) })),
      dismiss: (orderId) =>
        set((s) => ({ queue: s.queue.filter((item) => item.orderId !== orderId) })),
    }),
    {
      name: 'order-alert-store',
      storage: createJSONStorage(() => sessionStorage),
      // Only persist shownIds (as an array — Sets aren't JSON-serializable).
      // queue is intentionally excluded: pending dialogs should not reappear
      // after a refresh since the orders are still visible in the orders list.
      partialize: (state) => ({ shownIds: [...state.shownIds] }),
      merge: (persisted, current) => {
        const p = persisted as { shownIds?: string[] };
        return { ...current, shownIds: new Set(p?.shownIds ?? []) };
      },
    }
  )
);
